/*
 * Monsuri PDP — "Complete the ritual" ledger (the still-life + ledger cross-sell module).
 *
 * The CTA is a plain `.ajax-submit` form (theme-owned pipeline: in-flight disable, success state,
 * drawer refresh — see app.js.liquid) whose hidden inputs carry items[N][id] for the current
 * product + every checked pairing. Toggle rows live OUTSIDE the form; a checkbox toggles its
 * hidden inputs' `disabled` property (jQuery .serialize() drops disabled inputs, so unchecked rows
 * never submit). This file keeps the receipt honest and live:
 *   1. checkbox change → toggle the row's hidden inputs, re-sum the checked rows, relabel the total
 *      + the CTA, and disable the CTA when nothing is checked.
 *   2. buy-box variant change → the current-product row (row 0) follows the selected variant's price;
 *      an unavailable selection unchecks + disables that row rather than hiding the whole module.
 * Without JS the server-stamped default variant + total are already correct. Vanilla, idempotent.
 *
 * Set-mode note (ported from the old bundle bar): the variant <select>'s VALUE tracks the single
 * variant even in set mode (set mode strips only its NAME), so reading select.value stays correct.
 */
(function () {
  'use strict';

  function money(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(cents, format); } catch (e) { /* fall through */ }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function initLedger(ledger) {
    if (ledger.hasAttribute('data-xsell-ready')) return; // idempotent
    ledger.setAttribute('data-xsell-ready', '');

    var form = ledger.querySelector('.mds-pdp-xsell__form');
    var totalEl = ledger.querySelector('[data-xsell-total]');
    var totalLabelEl = ledger.querySelector('[data-xsell-total-label]');
    var btnSpan = ledger.querySelector('[data-xsell-btn]');
    var cta = ledger.querySelector('.mds-pdp-xsell__cta');
    var rows = ledger.querySelectorAll('.mds-pdp-xsell__row');
    if (!form || !totalEl || !btnSpan || !cta || !rows.length) return;

    var moneyFormat = ledger.getAttribute('data-money-format') || '${{amount}}';
    var addTemplate = ledger.getAttribute('data-add-template') || 'Add __COUNT__ · __TOTAL__';
    var labelFull = ledger.getAttribute('data-label-full') || 'The full ritual';
    var labelPartial = ledger.getAttribute('data-label-partial') || 'Your selection';

    // Toggle the hidden items[N] inputs that belong to a given row index.
    function setRowInputs(rowIndex, enabled) {
      var inputs = form.querySelectorAll('[data-xsell-item="' + rowIndex + '"]');
      for (var i = 0; i < inputs.length; i++) inputs[i].disabled = !enabled;
    }

    // The theme ajax add DISABLES the CTA + hides the label (`zoomOut`) / flashes a `.checkmark`
    // for its whole in-flight window (app.js.liquid beforeSend). While busy we must NOT re-enable
    // the button — recompute()'s count>0 path would defeat the theme's re-entrancy lock and let a
    // mid-flight checkbox toggle fire an overlapping second add with a different item set (codex
    // adversarial finding 1, 2026-07-10; same guard as pdp-complete-the-set.js). We leave the
    // button as the theme set it and re-assert the real state once the 350ms success timer frees it.
    var renorm = null;
    function themeBusy() {
      return !!(btnSpan.classList.contains('zoomOut')) ||
             !!(cta.querySelector('.checkmark.checkmark-active'));
    }

    function recompute() {
      var total = 0;
      var count = 0;
      var anyUnchecked = false;
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var check = row.querySelector('.mds-pdp-xsell__check');
        if (!check) continue;
        var price = parseInt(row.getAttribute('data-price'), 10);
        if (check.checked && !check.disabled) {
          if (!isNaN(price)) total += price;
          count += 1;
        } else {
          anyUnchecked = true;
        }
      }
      totalEl.textContent = money(total, moneyFormat);
      if (totalLabelEl) totalLabelEl.textContent = anyUnchecked ? labelPartial : labelFull;
      if (themeBusy()) {
        // Don't touch the label or the disabled state mid-add — retry after the theme's
        // 350ms success timer (or the error path) has settled the button.
        if (renorm) clearTimeout(renorm);
        renorm = setTimeout(recompute, 420);
        return;
      }
      btnSpan.textContent = addTemplate
        .replace('__COUNT__', String(count))
        .replace('__TOTAL__', money(total, moneyFormat));
      if (count === 0) {
        cta.setAttribute('disabled', 'disabled');
      } else {
        cta.removeAttribute('disabled');
      }
    }

    // Wire every checkbox: reflect its state onto the hidden inputs, then re-sum.
    for (var r = 0; r < rows.length; r++) {
      (function (row) {
        var check = row.querySelector('.mds-pdp-xsell__check');
        if (!check) return;
        var rowIndex = check.getAttribute('data-xsell-row');
        check.addEventListener('change', function () {
          setRowInputs(rowIndex, check.checked && !check.disabled);
          recompute();
        });
      })(rows[r]);
    }

    // ── Buy-box variant sync for the current-product row (row 0) ──────────────
    var currentRow = ledger.querySelector('.mds-pdp-xsell__row--current');
    var currentCheck = currentRow ? currentRow.querySelector('.mds-pdp-xsell__check') : null;
    var currentPriceEl = currentRow ? currentRow.querySelector('[data-xsell-price]') : null;
    var currentInput = form.querySelector('[data-pdp-bundle-current]');

    var scope = ledger.closest('.product-main') || document;
    var wrap = scope.querySelector('[data-product-form]');
    var formEl = wrap ? wrap.querySelector('form') : null;
    var select = formEl ? formEl.querySelector('select[name="id"], select[data-variants], input[name="id"]:not([data-pdp-set-id])') : null;

    var product = null;
    if (wrap && wrap.getAttribute('data-product')) {
      try { product = JSON.parse(wrap.getAttribute('data-product')); } catch (e) { /* no live sync */ }
    }

    function syncCurrent() {
      if (!select || !product || !product.variants || !currentRow || !currentCheck) return;
      var vid = parseInt(select.value, 10);
      for (var i = 0; i < product.variants.length; i++) {
        var v = product.variants[i];
        if (v.id !== vid) continue;
        if (v.available) {
          // Recovering from an unavailable selection: re-enable and restore the default check.
          // Already-enabled rows keep the shopper's own checked/unchecked choice.
          if (currentCheck.disabled) {
            currentCheck.disabled = false;
            currentCheck.checked = true;
          }
          if (currentInput) currentInput.value = v.id;
          setRowInputs('0', currentCheck.checked);
          currentRow.setAttribute('data-price', v.price);
          if (currentPriceEl) currentPriceEl.textContent = money(v.price, moneyFormat);
        } else {
          // An unavailable variant can't honestly join the ritual: drop just the current row.
          currentCheck.checked = false;
          currentCheck.disabled = true;
          setRowInputs('0', false);
          if (currentPriceEl) currentPriceEl.textContent = money(v.price, moneyFormat);
        }
        recompute();
        return;
      }
    }

    if (select) {
      select.addEventListener('change', function () { window.setTimeout(syncCurrent, 0); });
      syncCurrent();
    } else {
      recompute();
    }
  }

  function initAll() {
    var ledgers = document.querySelectorAll('[data-xsell-ledger]');
    for (var i = 0; i < ledgers.length; i++) initLedger(ledgers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll); // theme-editor re-render
})();
